import { supabase } from "./supabase";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

if (!VAPID_PUBLIC_KEY) {
  console.error("VITE_VAPID_PUBLIC_KEY environment variable is not set");
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!("Notification" in window)) {
    console.error("This browser does not support notifications");
    return false;
  }

  if (!("serviceWorker" in navigator)) {
    console.error("Service workers not supported");
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === "granted";
}

export async function isPushNotificationSupported(): Promise<boolean> {
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export async function subscribeToPushNotifications(): Promise<boolean> {
  try {
    const supported = await isPushNotificationSupported();
    if (!supported) {
      throw new Error("Push notifications not supported");
    }

    const permission = await requestNotificationPermission();
    if (!permission) {
      throw new Error("Notification permission denied");
    }

    const registration = await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      console.log("Unsubscribing from old subscription to create fresh one");
      await subscription.unsubscribe();
      subscription = null;
    }

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    const subscriptionJSON = subscription.toJSON();

    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      throw new Error("Not authenticated");
    }

    const { error } = await supabase.from("push_subscriptions").upsert(
      {
        user_id: user.user.id,
        endpoint: subscriptionJSON.endpoint!,
        p256dh: subscriptionJSON.keys!.p256dh!,
        auth: subscriptionJSON.keys!.auth!,
      },
      {
        onConflict: "user_id,endpoint",
      }
    );

    if (error) throw error;

    return true;
  } catch (error) {
    console.error("Error subscribing to push notifications:", error);
    return false;
  }
}

export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const subscriptionJSON = subscription.toJSON();

      await supabase
        .from("push_subscriptions")
        .delete()
        .eq("endpoint", subscriptionJSON.endpoint!);

      await subscription.unsubscribe();
    }

    return true;
  } catch (error) {
    console.error("Error unsubscribing from push notifications:", error);
    return false;
  }
}

export async function isSubscribedToPushNotifications(): Promise<boolean> {
  try {
    if (!("serviceWorker" in navigator)) return false;

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    return subscription !== null;
  } catch (error) {
    console.error("Error checking subscription status:", error);
    return false;
  }
}

export async function scheduleNotificationsForPlan(
  planId: string
): Promise<boolean> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(
      `${
        import.meta.env.VITE_SUPABASE_URL
      }/functions/v1/schedule-notifications`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ planId }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to schedule notifications");
    }

    const result = await response.json();
    return result.success;
  } catch (error) {
    console.error("Error scheduling notifications:", error);
    return false;
  }
}

export async function sendTestNotification(): Promise<boolean> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("Not authenticated");
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-notification`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          title: "Test Notification",
          body: "Push notifications are working!",
          tag: "test-notification",
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to send test notification");
    }

    return true;
  } catch (error) {
    console.error("Error sending test notification:", error);
    return false;
  }
}

export function getNotificationPermission(): NotificationPermission | null {
  if (!("Notification" in window)) return null;
  return Notification.permission;
}
